// Shared report→PDF wiring. Mirrors SprayLogExportDialog's useExportPdf +
// ExportModal flow so each report under src/pages/reports/ only maps its
// existing kpis/columns/rows and renders `modal`.
//
//   const reportPdf = useReportPdf();
//   <ExportButtons onExportCsv={...} onExportPdf={() => reportPdf.run({...})} />
//   {reportPdf.modal}

import { useCallback, useState, type ReactNode } from "react";
import type { RowInput, UserOptions } from "jspdf-autotable";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrencySettings } from "@/hooks/useCurrencySettings";
import { ExportModal, type ExportFilterChip } from "@/components/export/ExportModal";
import { useExportPdf, buildExportFilename } from "@/components/export/useExportPdf";
import { PdfReportBuilderV2 } from "@/lib/pdf/PdfReportBuilderV2";
import { buildReportPdfDoc, type ReportPdfColumn, type ReportPdfSection } from "@/lib/pdf/buildReportPdf";
import type { FilterChipV2, KpiCard } from "@/lib/pdf/reportBlocks";
import { cargarMarcaInforme } from "@/lib/marca/cabeceraInforme";
import { prefijoFichero } from "@/lib/marca/fichero";

export interface ReportPdfRunArgs {
  /** Slug for filename + cache key, e.g. "user-activity". */
  reportId: string;
  /** Localized report name (modal title + header subtitle). */
  reportTitle: string;
  orientation?: "portrait" | "landscape";
  dateStart?: string;
  dateEnd?: string;
  filters: FilterChipV2[];
  kpis: KpiCard[];
  columns: ReportPdfColumn[];
  rows: RowInput[];
  /** Optional per-cell styler for status pills / risk colours. */
  cellStyle?: UserOptions["didParseCell"];
  /** Extra values mixed into the cache key (so filter changes re-generate). */
  cacheExtra?: Record<string, unknown>;
  /** Preview note, e.g. "23 registros". */
  detailsLine?: string;
  /** Varias tablas en el mismo PDF (informes compuestos del asistente). */
  sections?: ReportPdfSection[];
  /**
   * Informes del asistente (F4 de «Personalizar informes»): los datos de la
   * empresa que este informe pide en su cabecera, en NOMBRES (`doc.cabecera`).
   * Sin él, la marca de siempre.
   */
  cabecera?: { campos: string[]; quitar?: string[] } | null;
}

export function useReportPdf() {
  const { t, i18n } = useTranslation();
  const { company } = useAuth();
  const { formatCurrency } = useCurrencySettings();
  const exportPdf = useExportPdf();
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState<{
    title: string;
    filters: ExportFilterChip[];
    summary: Array<{ label: string; value: string }>;
    details?: string;
  } | null>(null);

  const run = useCallback(
    async (args: ReportPdfRunArgs) => {
      const filters: ExportFilterChip[] = args.filters.map((f) => ({ label: f.label, value: f.value }));
      const summary = args.kpis.map((k) => ({ label: k.label, value: k.value }));
      setMeta({
        title: args.reportTitle,
        filters,
        summary,
        details: args.detailsLine ?? `${args.rows.length}`,
      });
      setOpen(true);

      /* La marca ANTES de la clave: si cambia, el PDF guardado no vale (Personalizar
         informes). Con la cabecera pedida en el chat (F4), su `version` ya lo lleva. */
      const marca = await cargarMarcaInforme(company?.id, args.cabecera);
      const filename = buildExportFilename(args.reportId, args.dateStart, args.dateEnd, prefijoFichero(marca));
      const cacheKey = JSON.stringify({
        id: args.reportId,
        marca: marca?.version ?? null,
        s: args.dateStart,
        e: args.dateEnd,
        n: args.rows.length,
        sec: args.sections?.map((x) => `${x.title}:${x.rows.length}:${x.columns.length}`),
        ...args.cacheExtra,
      });

      await exportPdf.generate(cacheKey, filename, () =>
        buildReportPdfDoc({
          orientation: args.orientation,
          marca,
          companyName: company?.name || "",
          reportSubtitle: args.reportTitle,
          dateRange:
            args.dateStart && args.dateEnd
              ? PdfReportBuilderV2.formatDateRange(args.dateStart, args.dateEnd, i18n.language)
              : undefined,
          filters: args.filters,
          kpis: args.kpis,
          columns: args.columns,
          rows: args.rows,
          sections: args.sections,
          cellStyle: args.cellStyle,
          formatCurrency,
          footer: {
            generated: `${t("pdfExport.generatedOn")} ${new Date().toLocaleString(i18n.language, {
              dateStyle: "medium",
              timeStyle: "short",
            })}`,
            page: t("pdfExport.page"),
            of: t("pdfExport.of"),
            madeWith: t("marcaInformes.pdf.madeWith"),
          },
        }),
      );
    },
    [company, formatCurrency, i18n.language, t, exportPdf],
  );

  const modal: ReactNode = (
    <ExportModal
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) exportPdf.reset();
      }}
      reportTitle={meta?.title ?? ""}
      filters={meta?.filters ?? []}
      summary={meta?.summary ?? []}
      detailsLine={meta?.details}
      state={exportPdf.state}
      error={exportPdf.error}
      canShare={exportPdf.canShare}
      onDownload={exportPdf.download}
      onOpen={exportPdf.open}
      onShare={exportPdf.share}
    />
  );

  return { run, modal };
}

export type { FilterChipV2, KpiCard, ReportPdfColumn };
